import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Calendar,
  Users,
  Trophy,
  FileText,
  Settings,
  Wrench,
  ChevronLeft,
  ChevronRight,
  Timer
} from 'lucide-react';
import clsx from 'clsx';
import packageJson from '../../../package.json';

const mainNavItems = [
  { path: '/', icon: Home, label: 'Dashboard' },
  { path: '/series', icon: Trophy, label: 'Series' },
];

const moduleNavItems = [];

const seriesNavItems = [
  { path: '/season', icon: Calendar, label: 'Season' },
  { path: '/events', icon: Calendar, label: 'Events' },
  { path: '/competitors', icon: Users, label: 'Competitors' },
  { path: '/results', icon: Trophy, label: 'Results' },
  { path: '/reports', icon: FileText, label: 'Reports' },
  { path: '/tools', icon: Wrench, label: 'Tools' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

function NavItem({ item, collapsed, seriesSlug }) {
  const location = useLocation();
  const fullPath = seriesSlug ? `/${seriesSlug}${item.path}` : item.path;
  const isActive = location.pathname === fullPath ||
    (item.path !== '/' && location.pathname.startsWith(fullPath));

  return (
    <NavLink
      to={fullPath}
      className={clsx(
        'flex items-center gap-3 px-3 py-2 rounded transition-colors duration-75 group',
        isActive
          ? 'bg-white/15 text-white border-l-2 border-white -ml-0.5'
          : 'text-slate-400 hover:text-white hover:bg-white/10'
      )}
      title={collapsed ? item.label : undefined}
    >
      <item.icon className={clsx('w-5 h-5 flex-shrink-0', isActive && 'text-white')} />
      {!collapsed && (
        <span className="text-sm font-medium truncate">{item.label}</span>
      )}
    </NavLink>
  );
}

function NavSection({ title, items, collapsed, seriesSlug }) {
  return (
    <div className="mb-5">
      {!collapsed && title && (
        <h3 className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {title}
        </h3>
      )}
      <nav className="space-y-1">
        {items.map((item) => (
          <NavItem key={item.path} item={item} collapsed={collapsed} seriesSlug={seriesSlug} />
        ))}
      </nav>
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle, currentSeries }) {
  return (
    <aside
      className={clsx(
        'fixed left-0 top-0 h-screen bg-slate-900 border-r border-slate-800 flex flex-col transition-all duration-200 z-40',
        collapsed ? 'w-14' : 'w-56'
      )}
      style={currentSeries ? {
        '--series-color': currentSeries.color,
        '--series-color-light': `${currentSeries.color}20`,
      } : undefined}
    >
      {/* Logo */}
      <div className={clsx(
        'h-14 flex items-center border-b border-slate-800 px-3',
        collapsed ? 'justify-center' : 'justify-between'
      )}>
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-wrg-blue-600 rounded flex items-center justify-center">
              <Timer className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-white tracking-wide">WRG RMS</h1>
              <p className="text-xs text-slate-500">Race Management</p>
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 bg-wrg-blue-600 rounded flex items-center justify-center">
            <Timer className="w-5 h-5 text-white" />
          </div>
        )}
      </div>

      {/* Current Series Indicator */}
      {currentSeries && !collapsed && (
        <div className="px-3 py-3 border-b border-slate-800 bg-slate-800/50">
          <div className="flex items-center gap-3">
            {currentSeries.logo ? (
              <img
                src={currentSeries.logo}
                alt={currentSeries.name}
                className="w-7 h-7 object-contain"
              />
            ) : (
              <div
                className="w-7 h-7 rounded flex items-center justify-center"
                style={{ backgroundColor: `${currentSeries.color}20` }}
              >
                <div
                  className="w-3.5 h-3.5 rounded-sm"
                  style={{ backgroundColor: currentSeries.color }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500">Current Series</p>
              <p className="text-sm font-medium text-white truncate">{currentSeries.shortName || currentSeries.name}</p>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed series indicator */}
      {currentSeries && collapsed && (
        <div className="px-2 py-3 border-b border-slate-800 flex justify-center">
          {currentSeries.logo ? (
            <img
              src={currentSeries.logo}
              alt={currentSeries.name}
              className="w-7 h-7 object-contain"
              title={currentSeries.name}
            />
          ) : (
            <div
              className="w-7 h-7 rounded flex items-center justify-center"
              style={{ backgroundColor: `${currentSeries.color}20` }}
              title={currentSeries.name}
            >
              <div
                className="w-3.5 h-3.5 rounded-sm"
                style={{ backgroundColor: currentSeries.color }}
              />
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4 px-2.5">
        <NavSection items={mainNavItems} collapsed={collapsed} />

        {currentSeries ? (
          <NavSection
            title="Series Management"
            items={seriesNavItems}
            collapsed={collapsed}
            seriesSlug={currentSeries.slug}
          />
        ) : (
          <NavSection
            title="Modules"
            items={moduleNavItems}
            collapsed={collapsed}
          />
        )}
      </div>

      {/* Collapse Toggle */}
      <div className="p-2.5 border-t border-slate-800">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Collapse</span>
            </>
          )}
        </button>
      </div>

      {/* Version Badge */}
      <div className={clsx(
        'px-2.5 pb-3 border-t border-slate-800',
        collapsed ? 'pt-2' : 'pt-3'
      )}>
        {collapsed ? (
          <div
            className="w-full flex justify-center"
            title={`v${packageJson.version} - Not for production use`}
          >
            <span className="text-[10px] font-bold text-amber-500">α</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide">Alpha</span>
            </span>
            <span className="text-[10px] text-slate-600">v{packageJson.version.replace('-alpha', '')}</span>
          </div>
        )}
      </div>
    </aside>
  );
}
